"use client";
import type { Variant } from "@components/commerce/product/product-tile/ProductTile";
import { ProductTile } from "@components/commerce/product/product-tile/ProductTile";
import { useGlobalModalContext } from "@components/GlobalModalProvider/GlobalModalProvider";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import type { AlgoliaItem } from "@lib/analytics/analyticsTypes";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { ModalTypes } from "@lib/enums/ModalTypes";
import { hasOneSize } from "@lib/utils/productUtility";
import {
    addToMyShoppingList,
    createMyShoppingList,
    removeFromMyShoppingList,
    showErrorToaster,
} from "@lib/utils/shoppingList-utility/shoppingListUtility";
import { useErrorToast } from "@lib/utils/store-utility/storeUtility";
import {
    useAddProductAndRemoveOldestFromWishlist,
    useAddProductToWishlist,
    useCreateMyWishlist,
    useGetMyActiveShoppingList,
    useLock,
    useRemoveProductFromWishlist,
} from "@store/hooks";
import { Box } from "@ui/components/layout/box/Box";
import { Carousel } from "@ui/components/navigation/carousel/Carousel";
import { useLocale } from "next-intl";
import dynamic from "next/dynamic";
import type { FC, ReactNode } from "react";
import { useRef } from "react";
import { useStore } from "store";

const ProductCarouselSkeleton = dynamic(
    () => import("@components/commerce/product/product-carousel/ProductCarouselSkeleton")
);

type ProductCarouselProps = {
    slideData: AlgoliaItem[];
    assistiveMessage: string;
    headline: string;
    slidesPerView?: number[];
    carouselType?: string;
    extraLink?: ReactNode;
    isLoading?: boolean;
    carouselHeight?: string[];
    tileWidth?: string[];
    id?: string;
    theme?: string;
};

export const ProductCarousel: FC<ProductCarouselProps> = (props) => {
    const {
        slideData,
        slidesPerView = [],
        headline,
        assistiveMessage,
        carouselType,
        extraLink = null,
        isLoading,
        carouselHeight,
        tileWidth,
        theme,
    } = props;
    const locale = useLocale();
    const errorToaster = useErrorToast();
    const setWishlistIsExecuted = useStore((state) => state.setWishlistIsExecuted);
    const wishlist = useStore((state) => state.data.me.wishlistInfo.wishlist);
    const getWishlist = useStore((state) => state.getWishlist);
    const lock = useLock();
    const { get: getMicrocopy } = useMicrocopy();
    const { showModal } = useGlobalModalContext();

    const executeCreateMyWishlist = useCreateMyWishlist();
    const executeGetMyActiveWishlist = useGetMyActiveShoppingList();
    const executeAddProductToWishlist = useAddProductToWishlist();
    const executeAddProductAndRemoveOldestFromWishlist = useAddProductAndRemoveOldestFromWishlist();
    const executeRemoveProductFromWishlist = useRemoveProductFromWishlist();

    const handleAddProductHitToWishlist = lock(async (hit) => {
        setWishlistIsExecuted(false);

        await executeGetMyActiveWishlist();
        if (!getWishlist()) {
            await createMyShoppingList(locale, getMicrocopy, executeCreateMyWishlist, errorToaster);
        }

        try {
            await addProductHitToWishlist(hit);
        } catch (error) {
            showErrorToaster(getMicrocopy, errorToaster);
        }
        setWishlistIsExecuted(true);
    });

    const addProductHitToWishlist = async (hit) => {
        const wishlist = getWishlist();
        const foundItem = wishlist?.lineItems.find(
            (x) =>
                x.variant?.sku === hit.objectID ||
                hit.variants?.some((v: Variant) => v.sku === x.variant?.sku)
        );
        const sku =
            hit.variants?.find((v: Variant) => hasOneSize(v.size))?.sku ||
            foundItem?.variant?.sku ||
            hit.objectID;

        if (foundItem) {
            await Promise.all([
                removeFromMyShoppingList(
                    locale,
                    foundItem,
                    wishlist,
                    executeRemoveProductFromWishlist
                ),
                AnalyticsClient.removeFromWishList(hit),
            ]);
        } else {
            await Promise.all([
                AnalyticsClient.addToWishList(hit),
                addToMyShoppingList(
                    locale,
                    wishlist,
                    sku,
                    executeAddProductToWishlist,
                    executeAddProductAndRemoveOldestFromWishlist
                ),
            ]);
        }
    };

    const intersectItems = useRef(new Set());
    const timerId = useRef(null);

    if (isLoading) {
        return <ProductCarouselSkeleton height={carouselHeight} width={tileWidth} />;
    } else if (!slideData?.length) {
        return null;
    }

    return (
        <Box w="100%" mt="0.2" data-testid="product-carousel">
            <Carousel
                theme={theme}
                title={headline}
                titleAriaLabel={assistiveMessage}
                showNavigation={false}
                slidesPerView={slidesPerView}
                gap={1}
                px={[0, 0, 0, 0]}
                py={[3, 3, 3, 3]}
                extraNavigationButtons={extraLink}
                h={carouselHeight}
            >
                {slideData?.map((slide: any) => {
                    if (!slide) {
                        return null;
                    }
                    const hasStockCount =
                        (slide.productColors || []).filter((color) => color.hasStock === true)
                            .length + 1;

                    const colorMicrocopy = `${hasStockCount} ${getMicrocopy(
                        CommonCMS.shared,
                        hasStockCount === 1 ? CommonCMS.color : CommonCMS.colors
                    )}`;

                    const foundItem = wishlist?.lineItems?.find(
                        (x) =>
                            x.variant?.sku === slide.objectID ||
                            slide.variants?.some((v: Variant) => v.sku === x.variant?.sku)
                    );

                    return (
                        <Box w={tileWidth} key={slide.objectID}>
                            <ProductTile
                                theme={theme}
                                key={slide.objectID}
                                productName={slide.productName}
                                variants={slide.variants}
                                articleNumber={slide.articleNumber}
                                colorCode={slide.colorCode}
                                image={slide.image}
                                imageLabel={slide.imageLabel}
                                altTagPattern={`${slide.pageTitle ?? slide.productName} - ${slide.dominantColor}`}
                                objectID={slide.objectID}
                                isAddingToWishlist={Boolean(foundItem)}
                                onAddToWishlist={async () => {
                                    const sku =
                                        slide.variants?.find((v: Variant) => hasOneSize(v.size))
                                            ?.sku ||
                                        foundItem?.variant?.sku ||
                                        slide.objectID;
                                    showModal(ModalTypes.addToWishList, {
                                        productKey: sku,
                                        selectedProductSku: sku,
                                    });
                                    handleAddProductHitToWishlist(slide);
                                }}
                                priceWithDiscount={slide.priceWithDiscount}
                                priceWithoutDiscount={slide.priceWithoutDiscount}
                                discountPercentage={slide.discountPercentage}
                                lowestPrice={slide.lowestPrice}
                                discountPercentageLowestPrice={slide.discountPercentageLowestPrice}
                                productColors={slide.productColors}
                                colorLabel={colorMicrocopy}
                                locale={locale}
                                backgroundColor={slide.backgroundColor}
                                handleTileClick={() => {
                                    AnalyticsClient.selectItem(
                                        slide,
                                        {
                                            position: slide.index,
                                            listName: carouselType,
                                        },
                                        null
                                    );
                                }}
                                customBadges={slide.badges}
                                descriptiveHeading={slide.descriptiveHeading}
                                w="full"
                                onInViewPort={(inView, item) => {
                                    clearTimeout(timerId.current);
                                    if (inView) {
                                        intersectItems.current.add(item);
                                    } else {
                                        intersectItems.current.delete(item);
                                    }
                                    if (intersectItems.current?.size) {
                                        timerId.current = setTimeout(() => {
                                            AnalyticsClient.viewItemsList(
                                                (slideData as AlgoliaItem[]).filter((item) =>
                                                    intersectItems.current.has(item.objectID)
                                                ),
                                                { listName: carouselType },
                                                slideData
                                            );
                                        }, 2000);
                                    }
                                }}
                                hasStock={slide.hasStock}
                                eaBadge={slide?.eaBadge}
                            />
                        </Box>
                    );
                })}
            </Carousel>
        </Box>
    );
};
